import { Link } from 'react-router-dom';
import {
  Blocks,
  Boxes,
  Briefcase,
  Check,
  ShoppingCart,
  Receipt,
  Users,
  Wallet,
  Wrench,
} from 'lucide-react';
import { usePageMeta } from '../usePageMeta';
import { SectionHeading } from '../components/SectionHeading';
import { CtaBanner } from '../components/CtaBanner';

const MODULE_TONES = ['g-blue', 'g-violet', 'g-pink', 'g-sun'] as const;

const MODULES = [
  {
    icon: Receipt,
    title: 'Sales',
    body: 'Quotes, orders, and invoicing — the full sales cycle in one place.',
  },
  {
    icon: ShoppingCart,
    title: 'Purchases',
    body: 'Purchase orders, supplier management, and incoming goods.',
  },
  {
    icon: Boxes,
    title: 'Inventory',
    body: 'Stock levels, movements, and valuation across locations.',
  },
  {
    icon: Wallet,
    title: 'Payroll',
    body: 'Salaries, deductions, and payouts for your whole team.',
  },
  {
    icon: Users,
    title: 'CRM',
    body: 'Contacts, pipelines, and follow-ups tied to real orders.',
  },
  {
    icon: Briefcase,
    title: 'Projects',
    body: 'Work, deadlines, and delivery tracked against the numbers.',
  },
] as const;

const COMPARE_ROWS = [
  ['You adapt your process to the software.', 'Lumen adapts to your process.'],
  ['Fixed modules, take it or leave it.', 'Start with what you need, add modules later.'],
  ['Customization means consultants and months.', 'Built to be configured — and extended by us when needed.'],
  ['One-size-fits-all workflows.', 'Workflows shaped to how your team already operates.'],
] as const;

export function Lumen(): React.JSX.Element {
  usePageMeta(
    'Lumen ERP — modular cloud ERP by Inafana',
    'Lumen is a modular, customizable cloud ERP SaaS: sales, purchases, inventory, payroll, CRM, and projects. Rigid ERPs make you work their way — Lumen is shaped to yours.',
  );

  return (
    <>
      <section className="product-hero">
        <div className="blob blob-blue" style={{ width: 560, height: 560, top: -160, left: '-6%' }} />
        <div className="blob blob-cyan" style={{ width: 440, height: 440, bottom: -120, right: '-4%' }} />
        <div className="container">
          <div className="product-hero-grid">
            <div className="product-hero-copy">
              <img
                className="product-mark"
                src="/assets/products/lumen.svg"
                alt="Lumen"
              />
              <h1 className="h1 reveal" data-delay="1">
                The ERP that <span className="grad">fits your business</span>.
                <br />
                Not the other way around.
              </h1>
              <p className="lede reveal" data-delay="2">
                Lumen runs your complete operations — sales, purchases, inventory,
                payroll, CRM, projects — as a modular cloud system. Start with the
                modules you need, shape them to your process, add more as you grow.
              </p>
              <div className="hero-ctas reveal" data-delay="3">
                <Link to="/#contact" className="btn btn-primary btn-lg">
                  Get a Lumen demo <span className="arrow">→</span>
                </Link>
                <Link to="/#work" className="btn btn-ghost btn-lg">
                  See who runs on it
                </Link>
              </div>
            </div>
            <div className="reveal" data-delay="2">
              <div className="compare">
                <div className="compare-row compare-head">
                  <div className="compare-cell old">Typical ERP</div>
                  <div className="compare-cell new">Lumen</div>
                </div>
                {COMPARE_ROWS.map(([old, next]) => (
                  <div key={next} className="compare-row">
                    <div className="compare-cell old">{old}</div>
                    <div className="compare-cell new">{next}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-surface">
        <div className="container">
          <SectionHeading
            anim="zoom"
            title={
              <>
                Your whole operation, <span className="si accent-text">one system</span>.
              </>
            }
            lede="Six core modules today — and because Lumen is modular, new ones can be added for your business specifically."
          />
          <div className="module-grid">
            {MODULES.map((m, i) => (
              <article key={m.title} className="module-card reveal" data-delay={(i % 3) + 1}>
                <div className={`icon ${MODULE_TONES[i % MODULE_TONES.length]}`} aria-hidden="true">
                  <m.icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="h3">{m.title}</h3>
                <p>{m.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <h2 className="h2">Flexible where other ERPs are <span className="si accent-text">rigid</span>.</h2>
              <p className="lede">
                Most ERPs ship one way of working and expect your business to comply.
                Lumen was built from years of custom ERP work — so flexibility is the
                foundation, not an afterthought.
              </p>
            </div>
            <ul className="feature-list reveal" data-delay="1">
              <li>
                <Blocks size={20} strokeWidth={1.8} />
                <div>
                  <b>Modular by design</b>
                  <span>
                    Run only the modules you need. Add sales today, payroll next
                    quarter — without migrations or re-implementation.
                  </span>
                </div>
              </li>
              <li>
                <Wrench size={20} strokeWidth={1.8} />
                <div>
                  <b>Deep customization</b>
                  <span>
                    Fields, workflows, and reports shaped to your process. When
                    configuration isn't enough, we extend the platform for you.
                  </span>
                </div>
              </li>
              <li>
                <Check size={20} strokeWidth={1.8} />
                <div>
                  <b>Proven in production</b>
                  <span>
                    Businesses like Elevani Industries run their daily operations on
                    Lumen — configured to their processes, not someone else's.
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <CtaBanner
        title={
          <>
            See Lumen <span className="si">shaped to your business</span>.
          </>
        }
        lede="Tell us how your operation runs today. We'll show you what it looks like in Lumen — module by module."
        primaryLabel="Get a Lumen demo"
      />
    </>
  );
}
